/* eslint-disable no-extend-native */
import { createApp, h, nextTick } from 'vue';
import { mapGetters } from 'vuex';
import { maskaDirective } from '@general/js/_maskaDirective';
import { useVuelidate } from '@vuelidate/core';
import axios from 'axios';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { setLogger } from '@general/js/utils/Logger';
import App from './App.vue';
import router from './router';
import store from './store';
import initFlatpickrDateInputs from '@general/js/utils/initFlatpickrDateInputs';

import { ERROR_UNAUTHORISED_ACCESS } from './router/routeNames';

import '../../src/styles/main.scss';

// Global introduction of the logger functionality.
setLogger();

// Re-init after SPA navigations (covers views that use plain <input> + placeholder)
if (router && typeof router.afterEach === 'function') {
  router.afterEach(() => {
    nextTick(() => {
      initFlatpickrDateInputs();
    });
  });
}

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    // if error object have response and status within, do error handling for given status
    switch (error?.response?.status) {
      case 403:
        router.push({ name: ERROR_UNAUTHORISED_ACCESS });
        break;

      default:
        break;
    }
    return Promise.reject(error);
  },
);

/**
 * to initialised custom prototype methods for token swap
 *
 * NOTE: each key must match with params key
 */
const tokenSwapForParam = {
  policyNumber: {
    swapName: 'tokenSwap',
    tokenNameRegex: RegExp(/\$memberNumber\$/g),
  },
  personCode: {
    swapName: 'personCodeTokenSwap',
    tokenNameRegex: RegExp(/\$thisPersonCode\$/g),
  },
  name: {
    swapName: 'memberNameTokenSwap',
    tokenNameRegex: RegExp(/\$thisPersonName\$/g),
  },
};
Object.values(tokenSwapForParam).forEach((obj) => {
  String.prototype[obj.swapName] = function () {
    return this;
  };
});

const app = createApp({
  beforeMount() {
    const params = this.$route.params;
    const currentMember = this.currentMember;

    // update token swap custom methods
    Object.keys(tokenSwapForParam).forEach((key) => {
      const thisSwap = tokenSwapForParam[key];
      String.prototype[thisSwap.swapName] = function () {
        let replaceWith;
        switch (key) {
          case 'personCode':
            replaceWith = currentMember.personCode;
            break;

          case 'name':
            replaceWith = `${currentMember.firstName} ${currentMember.lastName}`;
            break;

          default:
            replaceWith = params[key] ?? JSON.parse(window.sessionStorage.getItem('vuex'))?.thisPolicy;
            break;
        }
        return this.replace(thisSwap.tokenNameRegex, replaceWith);
      };
    });
  },

  watch: {
    '$route.params': {
      handler(newParams, oldParams) {
        // Only update custom token swap method for updated param to improve performance
        Object.keys(newParams).forEach((key) => {
          if (newParams[key] !== oldParams[key] && Object.keys(tokenSwapForParam).includes(key)) {
            let replaceWith;
            switch (key) {
              case 'personCode':
                replaceWith = this.currentMember.personCode;
                break;

              case 'name':
                replaceWith = `${this.currentMember.firstName} ${this.currentMember.lastName}`;
                break;

              default:
                replaceWith = newParams[key];
                break;
            }
            String.prototype[tokenSwapForParam[key].swapName] = function () {
              return this.replace(tokenSwapForParam[key].tokenNameRegex, replaceWith);
            };
          }
        });
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters({
      currentMember: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
    }),
  },

  methods: {
    getUrlParam(name, url) {
      let modifiedUrl = url;
      let modifiedName = name;
      if (!url) modifiedUrl = window.location.href;
      modifiedName = modifiedName.replace(/[[\]]/g, '\\$&');
      const regex = new RegExp(`[?&]${modifiedName}(=([^&#]*)|&|#|$)`);
      const results = regex.exec(modifiedUrl);
      if (!results) return null;
      if (!results[2]) return '';
      return decodeURIComponent(results[2].replace(/\+/g, ' '));
    },
  },

  render() {
    return h(App);
  },
});

app.mixin({ setup() { return { v$: useVuelidate() }; } });
app.directive('maska', maskaDirective);
app.use(router);
app.use(store);
app.mount('#member-portal');

// remove policy number stored in local storage before browser/tab closes
window.addEventListener('beforeunload', function () {
  window.localStorage.removeItem('currentPolicyNumber');
});
